import { FC } from 'react';
import {
  FieldErrors,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { FollowUpForm, SavedActivityInputPreview } from '../../lib/followUp';
import {
  StepResourceMode,
  getStepEditorActivities,
  getStepResourceMode,
} from '../../lib/mailSequence';
import Alert from '../../components/Alert';
import SectionLabel from '../../components/SectionLabel';
import { hasAssignedTeamMemberSigner, hasProviderFirstSigner } from './helpers';
import MailAlertMessage, {
  mailSegment,
  textSegment,
} from '../../components/MailAlertMessage';
import MailStepBadge from '../../components/MailStepBadge';
import ScheduleMailResourcesSection from './ScheduleMailResourcesSection';
import StepCustomResourcesEditor from './StepCustomResourcesEditor';

type PrependActivity = (activity: SavedActivityInputPreview) => void;

interface Props {
  stepIndex: number;
  defaultActivities: SavedActivityInputPreview[];
  stepActivitiesState: Record<number, SavedActivityInputPreview[]>;
  stepResourceModes: Record<number, StepResourceMode>;
  onSetStepActivities: (activities: SavedActivityInputPreview[]) => void;
  onSetStepResourceMode: (mode: StepResourceMode) => void;
  register: UseFormRegister<FollowUpForm>;
  watch: UseFormWatch<FollowUpForm>;
  setValue: UseFormSetValue<FollowUpForm>;
  errors: FieldErrors<FollowUpForm>;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<FollowUpForm, 'activities'>;
  prepend: PrependActivity;
}

const StepResourcePanel: FC<Props> = ({
  stepIndex,
  defaultActivities,
  stepActivitiesState,
  stepResourceModes,
  onSetStepActivities,
  onSetStepResourceMode,
  register,
  watch,
  setValue,
  errors,
  remove,
  update,
  prepend,
}) => {
  const isFirstStep = stepIndex === 0;
  const stepResourceMode = getStepResourceMode(stepResourceModes, stepIndex);
  const usesMailOneResources = stepResourceMode === 'inherit_mail_one';
  const editorActivities = getStepEditorActivities(
    stepActivitiesState,
    stepIndex,
  );
  const stepExtras = stepActivitiesState[stepIndex] ?? [];
  const effectiveStepActivities: SavedActivityInputPreview[] = isFirstStep
    ? defaultActivities
    : stepResourceMode === 'custom'
    ? stepExtras
    : [...defaultActivities, ...stepExtras];
  const showSignatureFirstWarning = hasProviderFirstSigner(
    effectiveStepActivities,
  );
  const showTeamMemberSignerHint = hasAssignedTeamMemberSigner(
    effectiveStepActivities,
  );

  const handleMailInheritChange = (checked: boolean) => {
    onSetStepResourceMode(checked ? 'inherit_mail_one' : 'custom');
  };

  const signatureFirstAlert = showSignatureFirstWarning ? (
    <div className="mt-4 mb-6">
      <Alert level="warning" message="Provider Signature Required">
        <p className="mt-1">
          <MailAlertMessage
            segments={[
              textSegment('One or more forms on '),
              mailSegment(stepIndex),
              textSegment(
                " need a provider's signature first. The client will still receive the Mail; those forms stay pending until the provider signs first.",
              ),
              ...(showTeamMemberSignerHint
                ? [
                    textSegment(
                      " You'll be able to choose which team member signs before enrolling.",
                    ),
                  ]
                : []),
            ]}
          />
        </p>
      </Alert>
    </div>
  ) : null;

  if (isFirstStep) {
    return (
      <div className="mt-6 border-t border-neutral-50 pt-6">
        <SectionLabel title="Attach resources to this Mail" />
        {signatureFirstAlert}
        <ScheduleMailResourcesSection
          activities={defaultActivities}
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          remove={remove}
          update={update}
          prepend={prepend}
          compactLayout
          hideSectionLabel
        />
      </div>
    );
  }

  return (
    <div className="mt-6 border-t border-neutral-50 pt-6">
      <SectionLabel title="Attach resources to this Mail" />
      {signatureFirstAlert}

      <label className="mt-4 flex cursor-pointer items-center gap-2.5">
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 rounded border-neutral-100 text-secondary-100 focus:ring-secondary-50"
          checked={usesMailOneResources}
          onChange={(e) => handleMailInheritChange(e.target.checked)}
        />
        <span className="inline-flex flex-wrap items-center gap-x-1 text-caption leading-5 text-neutral-150">
          <span>Attach the same resources, assessments, and activities as</span>
          <MailStepBadge stepIndex={0} />
        </span>
      </label>

      {usesMailOneResources && defaultActivities.length > 0 ? (
        <p className="mt-3 inline-flex flex-wrap items-center gap-x-1 text-small-caption leading-4 text-neutral-110">
          <span>
            Includes {defaultActivities.length} resource
            {defaultActivities.length === 1 ? '' : 's'} from
          </span>
          <MailStepBadge stepIndex={0} size="small" />
          <span>. Add more below.</span>
        </p>
      ) : null}

      <div className="mt-4">
        <StepCustomResourcesEditor
          activities={editorActivities}
          onChange={onSetStepActivities}
        />
      </div>
    </div>
  );
};

export default StepResourcePanel;
